import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../HireWithUs/Pages/Header&Footer/Header';
import Footer from '../HireWithUs/Pages/Header&Footer/Footer';
import { Container } from "@/Components/ui/container";
import { Stack } from "@/Components/ui/layout";
import { Card, CardContent } from "@/Components/ui/card";
import { Button } from "@/Components/ui/button";
import { Heading, Text } from "@/Components/ui/typography";
import { Badge } from "@/Components/ui/badge";
import { ArrowLeft } from "lucide-react";
import { getApiUrl } from '../config/api';

const BlogPost = () => {
  const { id } = useParams();

  const staticPosts = {
    1: {
      id: 1,
      title: "The Future of Recruitment: AI + Human Intelligence",
      content: `
        <p>Recruitment is no longer what it used to be. For decades, companies relied heavily on traditional processes — newspaper ads, stacks of resumes, long interview cycles, and gut-driven decisions. While these methods got the job done, they were often slow, inconsistent, and prone to bias. Today, the landscape has changed dramatically, thanks to the rise of Artificial Intelligence (AI). But here's the important truth: AI alone is not the future of hiring. The true game-changer lies in combining AI with human intelligence.</p>
        
        <p>AI is a powerhouse when it comes to data processing and efficiency. It can scan thousands of resumes in seconds, detect patterns, predict candidate fit, and highlight top talent with remarkable accuracy. For businesses facing tight timelines and high competition for talent, this level of speed and precision is invaluable. Imagine a recruiter who no longer has to spend hours manually filtering resumes, but instead receives a shortlist of high-potential candidates to focus on.</p>
        
        <p>Yet, despite its brilliance, AI has limitations. It cannot feel empathy, understand subtle human emotions, or grasp the cultural nuances of an organization. Human recruiters bring qualities to the table that machines simply cannot replicate: intuition, emotional intelligence, and the ability to connect with candidates on a personal level. These are critical in assessing whether someone will thrive within a team, adapt to a company's culture, or align with long-term organizational values.</p>
        
        <h2>The Power of AI in Recruitment</h2>
        <p>Artificial Intelligence has revolutionized the initial stages of recruitment by offering:</p>
        <ul>
          <li><strong>Speed and Efficiency:</strong> Processing thousands of applications in minutes</li>
          <li><strong>Pattern Recognition:</strong> Identifying qualified candidates based on data-driven criteria</li>
          <li><strong>Bias Reduction:</strong> Making decisions based on objective metrics rather than subjective impressions</li>
          <li><strong>Predictive Analytics:</strong> Forecasting candidate success and retention rates</li>
        </ul>
        
        <h2>The Human Touch in Hiring</h2>
        <p>While AI excels at data processing, human recruiters excel at:</p>
        <ul>
          <li><strong>Cultural Assessment:</strong> Understanding organizational fit and team dynamics</li>
          <li><strong>Emotional Intelligence:</strong> Reading between the lines during interviews</li>
          <li><strong>Relationship Building:</strong> Creating connections that go beyond qualifications</li>
          <li><strong>Strategic Thinking:</strong> Considering long-term organizational goals</li>
        </ul>
        
        <h2>The Perfect Partnership</h2>
        <p>The future of recruitment lies in leveraging the strengths of both AI and human intelligence:</p>
        
        <h3>1. AI Handles the Heavy Lifting</h3>
        <p>Let AI process applications, screen candidates, and identify top matches based on objective criteria. This frees up human recruiters to focus on what they do best.</p>
        
        <h3>2. Humans Make the Final Call</h3>
        <p>Use human judgment for final decisions, cultural fit assessments, and relationship building. AI provides the data, humans provide the wisdom.</p>
        
        <h3>3. Continuous Learning and Improvement</h3>
        <p>AI systems learn from human feedback, while humans learn from AI insights. This creates a virtuous cycle of improvement.</p>
        
        <h2>Looking Ahead</h2>
        <p>As we move forward, the most successful recruitment teams will be those that embrace both technologies. AI will continue to get smarter and more sophisticated, but it will never replace the human element that makes recruitment truly effective.</p>
        
        <p>The future isn't about choosing between AI and human intelligence — it's about creating the perfect partnership between them. When we combine the speed and precision of AI with the empathy and intuition of human recruiters, we create a recruitment process that is not just efficient, but truly transformative.</p>
        <p>When AI and human intelligence are combined, recruitment transforms into a smarter, faster, and fairer process. AI ensures consistency and scalability, while humans bring depth and context. Recruiters can now focus on what matters most — building relationships, understanding aspirations, and ensuring both company and candidate succeed in the long run.
The future of recruitment is not about replacing people with machines. It’s about empowering people with technology. Companies that embrace this balance will not only reduce bias and inefficiency but also unlock the ability to find and nurture the very best talent.<p/>

      `,
      author: "SearchJob.ai Team",
      date: "October 1, 2024",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&crop=center",
      category: "Recruitment & Technology"
    },
    2: {
      id: 2,
      title: "Why AI-Driven Screening Reduces Bias in Hiring (When Done Right)",
      content: `
        <p>Unconscious bias has always been one of the toughest challenges in recruitment. From the moment a recruiter reads a candidate's name, sees their address, or recognizes their college, biases — often unintended — can shape hiring decisions. These biases are not always malicious, but they can quietly undermine diversity and prevent organizations from hiring the best talent. This is where AI-driven screening comes in as a powerful solution — provided it's implemented correctly.</p>
        
        <p>AI, when designed with fairness in mind, focuses on measurable skills, qualifications, and performance indicators rather than personal attributes. Unlike humans, it doesn't get influenced by irrelevant details. Every candidate is judged against the same standardized criteria, leveling the playing field. This consistency is especially valuable for companies that want to promote diversity and inclusivity while still maintaining efficiency.</p>
        
        <p>For example, AI can anonymize resumes by removing personal identifiers, ensuring that a candidate's application is evaluated purely on merit. It can also use structured assessments to evaluate skills, such as coding challenges, problem-solving tests, or portfolio reviews, giving self-taught or non-traditional candidates a fair chance.</p>
        
        <h2>The Promise of Unbiased Evaluation</h2>
        <p>AI-driven screening offers several key advantages in reducing bias:</p>
        <ul>
          <li><strong>Consistent Criteria:</strong> Every candidate is evaluated using the same objective standards</li>
          <li><strong>Anonymized Processing:</strong> Personal identifiers are removed to prevent unconscious bias</li>
          <li><strong>Skill-Based Assessment:</strong> Focus on capabilities rather than background or appearance</li>
          <li><strong>Data-Driven Decisions:</strong> Objective metrics replace subjective impressions</li>
        </ul>
        
        <h2>The Critical Caveat: Training Data Matters</h2>
        <p>But here's the caveat: AI is only as good as the data it's trained on. If historical hiring data is biased, the AI system may unintentionally replicate or even amplify those biases. A classic example is training AI with past hiring records that favor one demographic over others — the algorithm will assume that preference is correct and continue the pattern.</p>
        
        <h2>Implementing Ethical AI Screening</h2>
        <p>This is why continuous monitoring, auditing, and refinement of AI systems is crucial. Companies must invest in:</p>
        <ul>
          <li><strong>Ethical AI Design:</strong> Building fairness into the algorithm from the ground up</li>
          <li><strong>Transparency:</strong> Understanding how decisions are made and being able to explain them</li>
          <li><strong>Human Oversight:</strong> Regular review and validation of AI recommendations</li>
          <li><strong>Bias Testing:</strong> Regular audits to identify and correct any emerging biases</li>
        </ul>
        
        <h2>Best Practices for Fair AI Screening</h2>
        <p>To ensure AI screening reduces bias effectively:</p>
        <ol>
          <li>Use diverse training data that represents various demographics and backgrounds</li>
          <li>Regularly audit AI decisions for patterns that might indicate bias</li>
          <li>Maintain human oversight and final decision-making authority</li>
          <li>Provide clear explanations for AI recommendations</li>
          <li>Continuously refine algorithms based on feedback and outcomes</li>
        </ol>
        
        <h2>The Competitive Advantage of Fair Hiring</h2>
        <p>When done right, AI-driven screening doesn't just make hiring faster — it makes it more equitable. It allows organizations to truly walk the talk when it comes to diversity and inclusion, while still securing the best candidates for the role.</p>
        
        <p>In a world where talent is global, diverse, and dynamic, fair and unbiased recruitment isn't just the right thing to do — it's a competitive advantage. Companies that successfully implement ethical AI screening will not only build more diverse teams but also gain access to a broader talent pool and improve their overall hiring outcomes.</p>
      `,
      author: "SearchJob.ai Team",
      date: "October 2, 2024",
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=400&fit=crop&crop=center",
      category: "AI & Diversity"
    },
    3: {
      id: 3,
      title: "Top 5 AI Recruitment Trends to Watch in 2025",
      content: `
        <p>At NewZenAlpha, we keep a close eye on how technology is shaping the recruitment landscape. As we move into 2025, AI is no longer just an add-on in hiring — it is becoming the backbone of modern recruitment strategies. Here are the five AI-powered trends we believe will define the future of talent acquisition:</p>
        
        <h2>1. Predictive Hiring Analytics</h2>
        <p>Recruitment will go beyond simple matching. AI will forecast not just who can do the job, but who will thrive in it long-term. By analyzing historical performance, retention patterns, and cultural indicators, predictive analytics will allow companies to hire with confidence.</p>
        
        <h2>2. Conversational AI Interviews</h2>
        <p>First-round screenings are evolving. AI-powered chatbots and virtual interviewers will handle initial assessments, making the process seamless for candidates while freeing up recruiters' time for higher-value tasks.</p>
        
        <h2>3. Bias-Detection Systems</h2>
        <p>As diversity and inclusion take center stage, AI tools designed to detect and flag bias in hiring processes will become essential. At NewZenAlpha, we see this as a critical step toward truly fair recruitment.</p>
        
        <h2>4. Skills-First Hiring</h2>
        <p>Resumes are losing their dominance. In 2025, AI will place far more emphasis on demonstrable skills — through coding tests, portfolio analysis, or certifications — rather than on traditional career histories. This shift will open doors for hidden talent pools often overlooked by conventional methods.</p>
        
        <h2>5. End-to-End Talent Insights</h2>
        <p>AI will not stop at recruitment. It will integrate seamlessly with employee performance data, career progression, and engagement metrics, giving companies a 360° view of talent. This will help organizations not only hire the right people but also grow and retain them.</p>
        
        <h2>The NewZenAlpha Advantage</h2>
        <p>At NewZenAlpha, we are committed to helping organizations stay ahead of these trends by delivering recruitment solutions that combine innovation with impact. For businesses that adopt these practices, the future of hiring will be smarter, faster, and far more inclusive.</p>
        
        <h2>Preparing for the Future</h2>
        <p>To stay competitive in 2025, organizations should:</p>
        <ul>
          <li><strong>Invest in AI Infrastructure:</strong> Build the technological foundation needed for these advanced capabilities</li>
          <li><strong>Train Teams:</strong> Ensure recruitment teams understand and can leverage AI tools effectively</li>
          <li><strong>Focus on Data Quality:</strong> Clean, diverse data is essential for AI to work optimally</li>
          <li><strong>Maintain Human Oversight:</strong> Use AI to enhance human decision-making, not replace it</li>
          <li><strong>Measure Impact:</strong> Track how AI implementation improves hiring outcomes and diversity</li>
        </ul>
        
        <p>The recruitment landscape is evolving rapidly, and AI is at the forefront of this transformation. Organizations that embrace these trends will not only improve their hiring efficiency but also gain a significant competitive advantage in attracting and retaining top talent.</p>
      `,
      author: "SearchJob.ai Team",
      date: "October 3, 2024",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=400&fit=crop&crop=center",
      category: "AI Trends & Recruitment"
    }
  };

  const [post, setPost] = useState(() => staticPosts[id] || null);
  const [loading, setLoading] = useState(() => !staticPosts[id]);

  useEffect(() => {
    // One of the three original hardcoded posts → use as-is.
    if (staticPosts[id]) { setPost(staticPosts[id]); setLoading(false); return; }
    // Otherwise it's a super-admin post → fetch it from the API.
    let alive = true;
    setLoading(true);
    fetch(getApiUrl(`/api/blogs/${id}`))
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (!alive) return;
        setPost(b ? {
          id: b.id,
          title: b.title,
          content: b.content || '',
          author: b.author || 'SearchJob.ai Team',
          date: b.created_at ? new Date(b.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '',
          image: b.image_url || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=400&fit=crop',
          category: b.category || 'Recruitment',
        } : null);
      })
      .catch(() => { if (alive) setPost(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-20 text-slate-500">Loading…</main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <Container>
            <div className="flex flex-col items-center justify-center py-20">
              <Heading level="h1" size="2xl" className="mb-4">Blog Post Not Found</Heading>
              <Text size="base" className="mb-6 text-slate-600">The blog post you're looking for doesn't exist.</Text>
              <Button asChild>
                <Link to="/blog">Back to Blog</Link>
              </Button>
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 py-8">
        <Container>
          <Stack spacing="lg">
            <article>
              <Stack spacing="lg">
                <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                </div>
                
                <Stack spacing="sm">
                  <Badge variant="outline" className="w-fit">{post.category}</Badge>
                  <Heading level="h1" size="3xl">{post.title}</Heading>
                </Stack>
                
                <Card>
                  <CardContent className="pt-6">
                    <div 
                      className="prose prose-slate max-w-none"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                      style={{
                        lineHeight: '1.75',
                      }}
                    />
                  </CardContent>
                </Card>
                
                <div className="pt-4">
                  <Button variant="outline" asChild>
                    <Link to="/blog">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Blog
                    </Link>
                  </Button>
                </div>
              </Stack>
            </article>
          </Stack>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPost;
